import { SITE } from '@cardengine/shared';

/**
 * Beer mats and a couple of face-down cards, scattered in the
 * empty margins either side of the page.
 *
 * Purely decorative: `aria-hidden`, `pointer-events: none`, and nothing here
 * is a control or conveys anything. A screen reader hears the page exactly as
 * it did before, and a click passes straight through to whatever is beneath.
 *
 * Drawn in CSS rather than loaded, so it costs no request and cannot fail. The
 * production CSP blocks external hosts anyway, and the one real asset the site
 * has — the deck — is a lazily-loaded chunk the home page should not be made
 * to wait on for decoration.
 *
 * **Anchored by the inner edge, not the centre.** Each piece is placed a fixed
 * distance *outward* from the content column and grows away from it, so it can
 * never encroach on the text however narrow the gutter gets. Positioning from
 * the centre line instead put two of them under the cards at 1600px and
 * through the copy at 1400px.
 */

/** Half the 64rem content column, plus its padding. Nothing comes inside it. */
const EDGE = 33;

interface Piece {
  side: 'left' | 'right';
  /** Distance beyond the content edge, in rem. */
  out: number;
  /** Distance from the top of the viewport, in rem. */
  y: number;
  rotate?: number;
}

const MATS: (Piece & { shape: 'round' | 'square'; small?: boolean })[] = [
  { side: 'left', out: 0.5, y: 7, shape: 'round', rotate: -9 },
  { side: 'left', out: 2.5, y: 25, shape: 'square', rotate: 7 },
  { side: 'right', out: 1, y: 12, shape: 'round', small: true, rotate: 12 },
  { side: 'right', out: 2, y: 30, shape: 'square', rotate: -5 },
];

const CARDS: Piece[] = [
  { side: 'left', out: 3.5, y: 45, rotate: -14 },
  { side: 'left', out: 0.8, y: 46.5, rotate: -4 },
  { side: 'right', out: 1.5, y: 44, rotate: 9 },
];

/** Pins the piece's inner edge `out` rem outside the content column. */
function place({ side, out, y, rotate }: Piece) {
  const anchor = `calc(50% + ${EDGE + out}rem)`;
  return {
    ...(side === 'left' ? { right: anchor } : { left: anchor }),
    top: `${y}rem`,
    transform: rotate ? `rotate(${rotate}deg)` : undefined,
  };
}

export function PubDressing() {
  return (
    <div className="pubdressing" aria-hidden="true">
            {MATS.map((m, i) => (
        <span
          key={`mat-${i}`}
          className={`mat mat--${m.shape} ${m.small ? 'mat--small' : ''}`}
          style={place(m)}
        >
          <span className="mat__rule" />
          <span className="mat__mark">♠</span>
          <span className="mat__name">{SITE.name}</span>
          <span className="mat__foot">free house</span>
        </span>
      ))}

      {/* One group with the fade on it, rather than fading each card.
          Opacity on a sibling makes overlapping cards blend into each other —
          you could see the lower card through the upper one. Applied to a
          parent, the children composite first and the stack reads as solid. */}
      <span className="dresscards">
        {CARDS.map((c, i) => (
          <span key={`card-${i}`} className="dresscard" style={place(c)} />
        ))}
      </span>
    </div>
  );
}
